import { useQuery } from '@apollo/client/react'
import { useState, useEffect, useRef, useMemo } from 'react'
import type { Me } from '@/entities'
import { routes } from '@/shared/constants'
import { ME } from '../api/queries'
import { fetchMyAdminProfile, type AdminRole } from '@/features/admin-users/adminUsers'

interface MeData {
  me: Me | null
}

const SYNTHETIC_ADMIN_ROLE = {
  id: 'portal_admin_synthetic',
  name: 'PORTAL_ADMIN',
  description: 'Administrador del portal (verificado vía portal-server)',
  context: 'PORTAL' as const,
  displayName: 'Admin del portal',
}

// El EDITOR de `admin_users`: todo lo del staff menos gestionar administradores
// (goal seguridad-remediacion 12; ver `PORTAL_EDITOR` en rolePermissions).
const SYNTHETIC_EDITOR_ROLE = {
  id: 'portal_editor_synthetic',
  name: 'PORTAL_EDITOR',
  description: 'Editor del portal (verificado vía portal-server)',
  context: 'PORTAL' as const,
  displayName: 'Editor del portal',
}

// Cache de página: evita múltiples llamadas HTTP cuando varios componentes
// montan useAuth() en el mismo ciclo. Se limpia sola en cada recarga (F5).
const adminCache = new Map<string, AdminRole | null>()

/**
 * Hook central de autenticación.
 *
 * Devuelve el usuario actual (query `me`). Si el JWT no trae PORTAL_ADMIN
 * pero el portal-server confirma que el email está en `admin_users` y está
 * activo, inyecta el rol sintético de su rol (`ADMIN` → PORTAL_ADMIN,
 * `EDITOR` → PORTAL_EDITOR) para que `can()` y el sidebar funcionen.
 */
export function useAuth() {
  const { data, loading, error } = useQuery<MeData>(ME, {
    fetchPolicy: 'cache-first',
  })

  const [portalAdminRole, setPortalAdminRole] = useState<AdminRole | null>(null)
  const checkedId = useRef<string | null>(null)

  useEffect(() => {
    const userId = data?.me?.id
    if (!userId) {
      setPortalAdminRole(null)
      checkedId.current = null
      return
    }
    if (checkedId.current === userId) return
    checkedId.current = userId

    // Si el JWT ya trae PORTAL_ADMIN (mocks o entorno con rol asignado), no llamar.
    if (data.me?.roles.some((r) => r.name === 'PORTAL_ADMIN')) return

    if (adminCache.has(userId)) {
      setPortalAdminRole(adminCache.get(userId) ?? null)
      return
    }

    fetchMyAdminProfile()
      .then((profile) => {
        const role = profile?.active ? profile.role : null
        adminCache.set(userId, role)
        setPortalAdminRole(role)
      })
      .catch(() => {
        adminCache.set(userId, null)
      })
  }, [data?.me?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const jwtUser = data?.me ?? null

  const user = useMemo((): Me | null => {
    if (!jwtUser) return null
    if (jwtUser.roles.some((r) => r.name === 'PORTAL_ADMIN') || !portalAdminRole) return jwtUser
    const synthetic = portalAdminRole === 'ADMIN' ? SYNTHETIC_ADMIN_ROLE : SYNTHETIC_EDITOR_ROLE
    return { ...jwtUser, roles: [...jwtUser.roles, synthetic] }
  }, [jwtUser, portalAdminRole])

  return {
    user,
    loading,
    error,
    redirectToLogin: () => window.location.assign(routes.public.login),
  }
}
